import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  BarChart3,
  Blocks,
  Check,
  Copy,
  Eye,
  Globe,
  Image,
  Instagram,
  LayoutTemplate,
  Link2,
  Loader2,
  Palette,
  Plus,
  Trash2,
  Type,
  Upload,
  User,
  Youtube,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '@nx/i18n';
import {
  PROFILE_ACCENTS,
  PROFILE_LOCATION_MAX,
  PROFILE_TAGLINE_MAX,
  PROFILE_THEMES,
  normalizeSlug,
} from '@nx/domain';
import { getProfilePublicPathPrefix, getProfilePublicUrl } from '@nx/shared';
import { APP_BASE_PATH } from '../../config/appBasePath';
import type { CreateProfileComponentInput } from '@nx/domain';
import { ComponentBlockEditor } from '../../components/profile/ComponentBlockEditor';
import { PublicProfile } from '../../components/profile/PublicProfile';
import { ProfileAvatar } from '../../components/profile/ProfileAvatar';
import { ProfileToastHost } from '../../components/profile/ProfileToastHost';
import { getProfilePageBackdrop } from '../../components/profile/profilePageBackdrop';
import { toProfileViewModel } from '../../components/profile/profileViewModel';
import { safeCssBackgroundImageUrl } from '../../utils/safeCssBackgroundImageUrl';
import { useEoToast } from '../../hooks/useEoToast';
import { issueProfilePreviewToken } from '../../services/profileCallables';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { pushToast } from '../../store/slices/eoUiSlice';
import {
  componentAddRequested,
  componentConfigPatched,
  componentConfigPersistRequested,
  componentEditEnded,
  componentEditStarted,
  componentRemoveRequested,
  componentReorderRequested,
  componentUpdateRequested,
  profileComponentImageRemoveRequested,
  profileComponentImageUploadRequested,
  profileAvatarUploadRequested,
  profileBackgroundRemoveRequested,
  profileBackgroundUploadRequested,
  profileCreateRequested,
  profileLoadRequested,
  profilePatch,
  profilePublishRequested,
  profileSaveRequested,
  profileThemePatch,
  profileUnpublishRequested,
} from '../../store/slices/profileSlice';

type ProfileEditorTab = 'basic' | 'theme' | 'comps' | 'stats';
type PreviewDevice = 'mobile' | 'desktop';
import '../../components/profile/profile-shared.css';
import './profile-editor.css';

const BIO_MAX = 220;

function blockPresets(
  t: (key: Parameters<ReturnType<typeof useI18n>['t']>[0]) => string,
): { key: string; labelKey: string; icon: typeof Link2; preset: CreateProfileComponentInput }[] {
  return [
    {
      key: 'social',
      labelKey: 'profile.block.social',
      icon: Instagram,
      preset: {
        type: 'social_link',
        config: {
          platform: 'instagram',
          label: t('profile.preset.socialLabel'),
          handle: t('profile.preset.socialHandle'),
          url: '#',
        },
      },
    },
    {
      key: 'link',
      labelKey: 'profile.block.link',
      icon: Link2,
      preset: {
        type: 'custom_link',
        config: {
          label: t('profile.preset.linkLabel'),
          note: t('profile.preset.linkNote'),
          url: '#',
        },
      },
    },
    {
      key: 'youtube',
      labelKey: 'profile.block.youtube',
      icon: Youtube,
      preset: {
        type: 'youtube',
        config: {
          label: t('profile.preset.youtubeLabel'),
          embedId: 'x',
          url: '#',
        },
      },
    },
    {
      key: 'instagram',
      labelKey: 'profile.block.igPost',
      icon: Instagram,
      preset: {
        type: 'instagram',
        config: {
          label: t('profile.preset.igLabel'),
          caption: t('profile.preset.igCaption'),
          url: '#',
        },
      },
    },
    {
      key: 'text',
      labelKey: 'profile.block.text',
      icon: Type,
      preset: {
        type: 'text_block',
        config: {
          label: t('profile.preset.textLabel'),
          content: t('profile.preset.textContent'),
        },
      },
    },
    {
      key: 'image',
      labelKey: 'profile.block.image',
      icon: LayoutTemplate,
      preset: {
        type: 'image_block',
        config: { label: t('profile.preset.imageLabel'), imageUrl: '' },
      },
    },
  ];
}

export function ProfileEditorPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { toasts } = useEoToast();
  const { identity } = useAppSelector((s) => s.auth);
  const { profile, components, loading, saving, mediaUploading, mediaUploadingComponentId } =
    useAppSelector((s) => s.profile);
  const [activeTab, setActiveTab] = useState<ProfileEditorTab>('basic');
  const [previewDevice, setPreviewDevice] = useState<PreviewDevice>('mobile');
  const [tagInput, setTagInput] = useState('');
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const backgroundInputRef = useRef<HTMLInputElement>(null);
  const blockTypes = useMemo(() => blockPresets(t), [t]);

  useEffect(() => {
    dispatch(profileLoadRequested());
  }, [dispatch]);

  const viewModel = useMemo(() => {
    if (!profile) return null;
    return toProfileViewModel(profile, components);
  }, [profile, components]);

  const previewBackdrop = useMemo(
    () => (viewModel ? getProfilePageBackdrop(viewModel) : undefined),
    [viewModel],
  );

  const safeBackgroundUrl = useMemo(
    () => safeCssBackgroundImageUrl(profile?.themeConfig.backgroundImageUrl),
    [profile?.themeConfig.backgroundImageUrl],
  );

  const published = profile?.status === 'published';
  const profilePathPrefix = getProfilePublicPathPrefix(APP_BASE_PATH);
  const profilePublicUrl = profile
    ? getProfilePublicUrl(profile.slug, undefined, APP_BASE_PATH)
    : '';
  const visibleCount = components.filter((c) => c.visible).length;
  const totalClicks = components.reduce((s, c) => s + c.clickCount, 0);
  const maxClick = Math.max(...components.map((c) => c.clickCount), 1);

  const handleCreate = () => {
    const title =
      identity?.displayName ?? identity?.email ?? t('profile.create.defaultTitle');
    const slug = normalizeSlug(title) || 'my-profile';
    dispatch(
      profileCreateRequested({
        slug,
        title,
        description: '',
        tags: [],
      }),
    );
  };

  const saveDraft = () => {
    if (!profile) return;
    dispatch(
      profileSaveRequested({
        title: profile.title,
        description: profile.description,
        tagline: profile.tagline,
        location: profile.location,
        slug: profile.slug,
        tags: profile.tags,
        themeConfig: profile.themeConfig,
      }),
    );
  };

  const copyLink = async () => {
    if (!profile) return;
    await navigator.clipboard.writeText(getProfilePublicUrl(profile.slug, undefined, APP_BASE_PATH));
  };

  const moveComponent = (index: number, dir: -1 | 1) => {
    const ids = components.map((c) => c.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    dispatch(componentReorderRequested(ids));
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF8F5]">
        <Loader2 className="h-8 w-8 animate-spin text-[#78665A]" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] px-4 py-10">
        <div className="profile-create">
          <h1 className="text-2xl font-bold text-[#1A1A1A]">{t('profile.create.title')}</h1>
          <p className="mt-2 text-sm text-[#78665A]">{t('profile.create.subtitle')}</p>
          <button type="button" className="tp-btn tp-btn-primary mt-6" onClick={handleCreate} disabled={saving}>
            {t('profile.create.action')}
          </button>
          <p className="mt-4">
            <Link to="/dashboard" className="text-sm text-[#78665A] underline">
              {t('common.back')}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="flex h-[60px] items-center border-b border-[#3D3328]/10 bg-white px-5">
        <Link to="/dashboard" className="text-sm font-semibold text-[#78665A]">
          {t('common.back')}
        </Link>
        <div className="mx-auto text-center">
          <strong className="block text-sm font-extrabold">N·Excelsior</strong>
          <small className="text-[10.5px] text-[#78665A]">{t('profile.editor.subtitle')}</small>
        </div>
      </header>

      <div className="profile-editor">
        <div className="profile-editor-panel">
          <div className="profile-editor-panel-head">
            <span className={`profile-editor-status ${published ? 'published' : 'draft'}`}>
              <span className="dot" />
              {published ? t('profile.status.published') : t('profile.status.draft')}
            </span>
            <p className="profile-editor-slug">
              <Globe className="h-3.5 w-3.5" />
              {profilePathPrefix}
              <b>{profile.slug}</b>
            </p>
          </div>

          <div className="profile-editor-tabs">
            {(
              [
                ['basic', 'profile.tab.basic', User],
                ['theme', 'profile.tab.theme', Palette],
                ['comps', 'profile.tab.content', Blocks],
                ['stats', 'profile.tab.stats', BarChart3],
              ] as const
            ).map(([id, label, Icon]) => (
              <button
                key={id}
                type="button"
                className={`profile-editor-tab ${activeTab === id ? 'on' : ''}`}
                onClick={() => setActiveTab(id)}
              >
                <Icon className="h-4 w-4" strokeWidth={2} />
                {t(label)}
              </button>
            ))}
          </div>

          <div className="profile-editor-body">
            {activeTab === 'basic' && (
              <>
                <div className="profile-editor-group">
                  <p className="profile-editor-group-t">{t('profile.group.identity')}</p>
                  <div className="profile-editor-avatar-row">
                    <ProfileAvatar
                      name={profile.title}
                      color={profile.themeConfig.primaryColor}
                      size={60}
                      square={profile.themeConfig.templateId === 'terra'}
                      imageUrl={profile.avatarUrl}
                    />
                    <div className="flex gap-2">
                      <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (file) dispatch(profileAvatarUploadRequested({ file }));
                        }}
                      />
                      <button
                        type="button"
                        className="tp-btn tp-btn-outline tp-btn-sm"
                        onClick={() => avatarInputRef.current?.click()}
                        disabled={mediaUploading === 'avatar'}
                      >
                        {mediaUploading === 'avatar' ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                        ) : (
                          <Upload className="h-3.5 w-3.5" strokeWidth={2} />
                        )}
                        {t('profile.action.upload')}
                      </button>
                    </div>
                  </div>
                  <div className="profile-editor-field">
                    <label>{t('profile.field.displayName')}</label>
                    <input
                      className="profile-editor-input"
                      value={profile.title}
                      onChange={(e) => dispatch(profilePatch({ title: e.target.value }))}
                    />
                  </div>
                  <div className="profile-editor-field">
                    <label>{t('profile.field.role')}</label>
                    <input
                      className="profile-editor-input"
                      maxLength={PROFILE_TAGLINE_MAX}
                      value={profile.tagline ?? ''}
                      placeholder={t('profile.field.taglinePlaceholder')}
                      onChange={(e) => dispatch(profilePatch({ tagline: e.target.value }))}
                    />
                  </div>
                  <div className="profile-editor-field">
                    <label>{t('profile.field.bio')}</label>
                    <textarea
                      className="profile-editor-textarea"
                      maxLength={BIO_MAX}
                      value={profile.description ?? ''}
                      onChange={(e) => dispatch(profilePatch({ description: e.target.value }))}
                    />
                    <p className="profile-editor-help">
                      <span>{t('profile.field.bioHelp')}</span>
                      <span>
                        {(profile.description ?? '').length}/{BIO_MAX}
                      </span>
                    </p>
                  </div>
                  <div className="profile-editor-field">
                    <label>{t('profile.field.location')}</label>
                    <input
                      className="profile-editor-input"
                      maxLength={PROFILE_LOCATION_MAX}
                      value={profile.location ?? ''}
                      placeholder={t('profile.field.locationPlaceholder')}
                      onChange={(e) => dispatch(profilePatch({ location: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="profile-editor-group">
                  <p className="profile-editor-group-t">{t('profile.group.publicUrl')}</p>
                  <div className="profile-editor-field">
                    <label>{t('profile.field.slug')}</label>
                    <div className="profile-editor-input-pre">
                      <span>{profilePathPrefix}</span>
                      <input
                        value={profile.slug}
                        onChange={(e) =>
                          dispatch(profilePatch({ slug: normalizeSlug(e.target.value) }))
                        }
                      />
                    </div>
                    <p className="profile-editor-help ok">
                      <span className="inline-flex items-center gap-1">
                        <Check className="h-3.5 w-3.5" strokeWidth={2} />
                        {t('profile.slug.available')}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="profile-editor-group">
                  <p className="profile-editor-group-t">{t('profile.group.tags')}</p>
                  <div className="profile-editor-tags">
                    {profile.tags.map((tag) => (
                      <span key={tag} className="profile-editor-tag">
                        {tag}
                        <button
                          type="button"
                          onClick={() =>
                            dispatch(profilePatch({ tags: profile.tags.filter((x) => x !== tag) }))
                          }
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      className="profile-editor-tag-add"
                      onClick={() => {
                        const next = tagInput.trim() || window.prompt('Tag');
                        if (next?.trim()) {
                          dispatch(profilePatch({ tags: [...profile.tags, next.trim()] }));
                          setTagInput('');
                        }
                      }}
                    >
                      <Plus className="h-3 w-3" strokeWidth={2} />
                      {t('profile.tag.add')}
                    </button>
                  </div>
                </div>
              </>
            )}

            {activeTab === 'theme' && (
              <>
                <div className="profile-editor-group">
                  <p className="profile-editor-group-t">
                    {t('profile.group.template')} · {PROFILE_THEMES.length}
                  </p>
                  <div className="profile-editor-themes">
                    {PROFILE_THEMES.map((theme) => (
                      <button
                        key={theme.id}
                        type="button"
                        className={`profile-editor-theme ${profile.themeConfig.templateId === theme.id ? 'on' : ''}`}
                        onClick={() => dispatch(profileThemePatch({ templateId: theme.id }))}
                      >
                        <span className="profile-editor-theme-prev">
                          {theme.swatch.map((c) => (
                            <span key={c} style={{ background: c }} />
                          ))}
                        </span>
                        <span className="profile-editor-theme-meta">
                          <strong>{theme.name}</strong>
                          <small>{theme.mood}</small>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="profile-editor-group">
                  <p className="profile-editor-group-t">{t('profile.group.accent')}</p>
                  <div className="profile-editor-swatches">
                    {PROFILE_ACCENTS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`profile-editor-swatch ${profile.themeConfig.primaryColor === c ? 'on' : ''}`}
                        style={{ background: c }}
                        onClick={() =>
                          dispatch(profileThemePatch({ primaryColor: c, buttonColor: c }))
                        }
                        aria-label={c}
                      />
                    ))}
                  </div>
                  <p className="profile-editor-help">
                    <span>{t('profile.accent.help')}</span>
                  </p>
                </div>
                <div className="profile-editor-group">
                  <p className="profile-editor-group-t">{t('profile.group.background')}</p>
                  <div
                    className={`profile-editor-bg-preview${safeBackgroundUrl ? ' has-image' : ''}`}
                  >
                    {safeBackgroundUrl ? (
                      <img src={safeBackgroundUrl} alt={t('profile.background.previewAlt')} />
                    ) : (
                      <span className="profile-editor-bg-preview-ph">
                        <Image className="h-5 w-5" strokeWidth={2} />
                      </span>
                    )}
                    {mediaUploading === 'background' ? (
                      <span className="profile-editor-bg-preview-loading">
                        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
                      </span>
                    ) : null}
                  </div>
                  <input
                    ref={backgroundInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) dispatch(profileBackgroundUploadRequested({ file }));
                    }}
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="tp-btn tp-btn-outline tp-btn-sm"
                      onClick={() => backgroundInputRef.current?.click()}
                      disabled={mediaUploading === 'background' || mediaUploading === 'background-remove'}
                    >
                      {mediaUploading === 'background' ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                      ) : (
                        <Upload className="h-3.5 w-3.5" strokeWidth={2} />
                      )}
                      {t('profile.action.uploadBackground')}
                    </button>
                    {safeBackgroundUrl ? (
                      <button
                        type="button"
                        className="tp-btn tp-btn-ghost tp-btn-sm"
                        onClick={() => dispatch(profileBackgroundRemoveRequested())}
                        disabled={mediaUploading === 'background' || mediaUploading === 'background-remove'}
                      >
                        {mediaUploading === 'background-remove' ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                        )}
                        {t('profile.action.removeBackground')}
                      </button>
                    ) : null}
                  </div>
                  <p className="profile-editor-help">
                    <span>{t('profile.background.help')}</span>
                  </p>
                </div>
              </>
            )}

            {activeTab === 'comps' && (
              <>
                <div className="profile-editor-group">
                  <p className="profile-editor-group-t">{t('profile.group.addBlock')}</p>
                  <div className="profile-editor-add-grid">
                    {blockTypes.map((b) => (
                      <button
                        key={b.key}
                        type="button"
                        className="profile-editor-add"
                        onClick={() => dispatch(componentAddRequested(b.preset))}
                      >
                        <b.icon className="h-5 w-5" strokeWidth={2} />
                        <span>{t(b.labelKey as Parameters<typeof t>[0])}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="profile-editor-group">
                  <p className="profile-editor-group-t">
                    {t('profile.group.blocks')} ·{' '}
                    {t('profile.blocks.shown')
                      .replace('{visible}', String(visibleCount))
                      .replace('{total}', String(components.length))}
                  </p>
                  <div className="profile-editor-clist">
                    {components.map((c, i) => (
                      <ComponentBlockEditor
                        key={c.id}
                        component={c}
                        index={i}
                        total={components.length}
                        imageUploading={
                          mediaUploading === 'component-image' && mediaUploadingComponentId === c.id
                        }
                        onMove={moveComponent}
                        onToggle={(visible) =>
                          dispatch(componentUpdateRequested({ componentId: c.id, input: { visible } }))
                        }
                        onRemove={() => dispatch(componentRemoveRequested(c.id))}
                        onEditStart={() => dispatch(componentEditStarted(c.id))}
                        onEditEnd={() => dispatch(componentEditEnded(c.id))}
                        onConfigChange={(config) =>
                          dispatch(componentConfigPatched({ componentId: c.id, config }))
                        }
                        onPersist={() => dispatch(componentConfigPersistRequested(c.id))}
                        onImageUpload={(file) =>
                          dispatch(profileComponentImageUploadRequested({ componentId: c.id, file }))
                        }
                        onImageRemove={() =>
                          dispatch(profileComponentImageRemoveRequested({ componentId: c.id }))
                        }
                      />
                    ))}
                  </div>
                </div>
              </>
            )}

            {activeTab === 'stats' && (
              <>
                <div className="profile-editor-group">
                  <p className="profile-editor-group-t">{t('profile.group.overview')}</p>
                  <div className="profile-editor-stat-grid">
                    <div className="profile-editor-stat accent">
                      <div className="k">{profile.viewCount.toLocaleString()}</div>
                      <div className="l">
                        <Eye className="h-3.5 w-3.5" strokeWidth={2} />
                        {t('profile.stats.views')}
                      </div>
                    </div>
                    <div className="profile-editor-stat">
                      <div className="k">{totalClicks.toLocaleString()}</div>
                      <div className="l">{t('profile.stats.clicks')}</div>
                    </div>
                    <div className="profile-editor-stat">
                      <div className="k">
                        {profile.viewCount > 0
                          ? ((totalClicks / profile.viewCount) * 100).toFixed(1)
                          : '0.0'}
                        %
                      </div>
                      <div className="l">{t('profile.stats.ctr')}</div>
                    </div>
                    <div className="profile-editor-stat">
                      <div className="k">{visibleCount}</div>
                      <div className="l">{t('profile.stats.liveBlocks')}</div>
                    </div>
                  </div>
                </div>
                <div className="profile-editor-group">
                  <p className="profile-editor-group-t">{t('profile.group.clicksByBlock')}</p>
                  {[...components]
                    .filter((c) => c.clickCount)
                    .sort((a, b) => b.clickCount - a.clickCount)
                    .map((c) => (
                      <div key={c.id} className="mb-2">
                        <div className="flex justify-between text-xs font-semibold">
                          <span>{c.config.label}</span>
                          <span>{c.clickCount.toLocaleString()}</span>
                        </div>
                        <div
                          className="profile-editor-click-bar mt-1"
                          style={
                            { '--click-pct': `${(c.clickCount / maxClick) * 100}%` } as CSSProperties
                          }
                        >
                          <div className="profile-editor-click-fill" />
                        </div>
                      </div>
                    ))}
                </div>
              </>
            )}
          </div>

          <div className="profile-editor-foot">
            {published && (
              <div className="flex items-center gap-2 rounded-[10px] border border-[#3D3328]/10 bg-[#F5F2ED] px-3 py-2">
                <code className="flex-1 truncate text-xs text-[#78665A]" title={profilePublicUrl}>
                  {profilePublicUrl}
                </code>
                <button type="button" className="tp-btn tp-btn-ghost tp-btn-sm" onClick={copyLink}>
                  <Copy className="h-3.5 w-3.5" strokeWidth={2} />
                </button>
              </div>
            )}
            <div className="profile-editor-foot-row">
              <button
                type="button"
                className="tp-btn tp-btn-outline"
                onClick={async () => {
                  if (published) {
                    navigate(`/u/${profile.slug}`);
                    return;
                  }
                  try {
                    const { token } = await issueProfilePreviewToken(profile.id);
                    window.open(`/preview/${token}`, '_blank', 'noopener,noreferrer');
                  } catch {
                    dispatch(pushToast({ msg: 'profile.toast.previewFailed', kind: 'danger' }));
                  }
                }}
              >
                <Eye className="h-4 w-4" strokeWidth={2} />
                {t('profile.action.preview')}
              </button>
              <button
                type="button"
                className="tp-btn tp-btn-ghost tp-btn-sm"
                onClick={saveDraft}
                disabled={saving}
              >
                {t('profile.action.saveDraft')}
              </button>
              {published ? (
                <button
                  type="button"
                  className="tp-btn tp-btn-outline"
                  onClick={() => dispatch(profileUnpublishRequested())}
                  disabled={saving}
                >
                  {t('profile.action.unpublish')}
                </button>
              ) : (
                <button
                  type="button"
                  className="tp-btn tp-btn-primary"
                  onClick={() => dispatch(profilePublishRequested())}
                  disabled={saving}
                >
                  {t('profile.action.publish')}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="profile-editor-preview">
          <div className="profile-editor-preview-bar">
            <span className="lbl">
              <Eye className="h-4 w-4" strokeWidth={2} />
              {t('profile.preview.label')}
            </span>
            <div className="profile-editor-dev-toggle">
              <button
                type="button"
                className={previewDevice === 'mobile' ? 'on' : ''}
                onClick={() => setPreviewDevice('mobile')}
              >
                {t('profile.preview.mobile')}
              </button>
              <button
                type="button"
                className={previewDevice === 'desktop' ? 'on' : ''}
                onClick={() => setPreviewDevice('desktop')}
              >
                {t('profile.preview.desktop')}
              </button>
            </div>
          </div>
          <div
            className="profile-editor-canvas"
            style={previewBackdrop ? ({ backgroundColor: previewBackdrop } as CSSProperties) : undefined}
          >
            <div className={`ph-frame ${previewDevice === 'desktop' ? 'desktop' : ''}`}>
              <div className="ph-screen">
                {viewModel ? <PublicProfile profile={viewModel} /> : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProfileToastHost toasts={toasts} />
    </div>
  );
}

